import { useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { parseAge, vitalConfigForAge } from "@/lib/vitals";
import { useCaseActions } from "@/hooks/useCaseActions";
import { useWorkflowState, emptyPatient, emptyFindings } from "@/hooks/useWorkflowState";
import { useWorkflowSteps } from "@/hooks/useWorkflowSteps";
import { useWorkflowPersistence, clearDraft } from "@/hooks/workflowDraft";
import { toast } from "sonner";

export function useWorkflow() {
  const { user, logout } = useAuth();
  const s = useWorkflowState();

  const draftNotified = useRef(false);
  useEffect(() => {
    if (!s.hadSavedDraft || draftNotified.current) return undefined;
    draftNotified.current = true;
    // Defer past the mount commit so Sonner's <Toaster/> has subscribed on a
    // cold reload (rAF fires before passive effects, so it misses the race).
    const id = setTimeout(() => {
      toast.info("Draft restored — continuing your in-progress case. Use \"New case\" to start over.");
    }, 250);
    return () => clearTimeout(id);
  }, [s.hadSavedDraft]);

  const ageYears = parseAge(s.patient.age);
  const isPediatric = ageYears != null && ageYears < 18;
  const vitalCfg = vitalConfigForAge(ageYears);
  const redFlag = s.tentative?.emergency_flag ? (s.tentative.emergency_reason || "Time-critical finding detected.") : "";

  useWorkflowPersistence({
    model: s.model, step: s.step, patient: s.patient, questions: s.questions, answers: s.answers,
    tentative: s.tentative, signs: s.signs, signResp: s.signResp, findings: s.findings, scores: s.scores,
    differentials: s.differentials, investigations: s.investigations, reports: s.reports, imaging: s.imaging, final: s.final,
  });

  const run = async (label, fn) => {
    s.setLoading(label);
    try {
      return await fn();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      throw e;
    } finally {
      s.setLoading("");
    }
  };

  const updPatient = (k) => (e) => s.setPatient((p) => ({ ...p, [k]: e.target.value }));

  const addSnapshotToNotes = (text) => {
    if (!text) return;
    s.setFindings((f) => {
      const existing = (f.examination_notes || "").trim();
      return { ...f, examination_notes: existing ? `${existing}\n\n${text}` : text };
    });
    toast.success("Summary added to case notes");
  };

  const steps = useWorkflowSteps(s, run, ageYears);
  const actions = useCaseActions({
    patient: s.patient, findings: s.findings, final: s.final, redFlag,
    differentials: s.differentials, investigations: s.investigations, reports: s.reports, imaging: s.imaging,
  });

  const reset = () => {
    clearDraft();
    s.setStep(0); s.setPatient(emptyPatient); s.setQuestions(null); s.setAnswers({}); s.setTentative(null);
    s.setSigns(null); s.setSignResp({}); s.setFindings(emptyFindings); s.setScores(null);
    s.setDifferentials(null); s.setInvestigations(null); s.setReports([]); s.setImaging([]); s.setFinal(null);
    actions.setSavedId(null); s.setAnswersReminded(false); s.setSignsReminded(false);
  };

  return {
    ...s, ...steps, ...actions,
    user, logout, updPatient, redFlag, addSnapshotToNotes,
    ageYears, isPediatric, vitalCfg, reset,
  };
}
